import { isEmpty, isPlainObject, isArray, isNil } from "lodash";
import { useState, useEffect } from "react";
import {
  Outlet,
  useParams,
  generatePath,
  useNavigate,
  useSearchParams,
  NavLink,
} from "react-router-dom";
import {
  Alert,
  TextField,
  Box,
  Skeleton,
  Slide,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { backOff } from "exponential-backoff";
import DownloadPdfButton from "./BusinessAttributes/DownloadPdfButton";
import UnauthorizedErrorDisplay from "./UnauthorizedErrorDisplay";
import UnsuccessfulErrorDisplay from "./UnsuccessfulErrorDisplay";
import NetworkFailureToast from "./NetworkFailureToast";
import ResultsTabs from "./ResultsTabs";
import WebOnly from "./WebOnly";
import { useTrueBizApi } from "../api";
import APICompanyLookup from "../types/APICompanyLookup";
import { humanizeDateString } from "../utilities/formatting";

/**
 * Checks to see if the `response_data` returned by the company lookup
 * is a skeleton populated with null/undefined/false values. A skeleton is
 * returned when a search is still being processed on the backend. For now,
 * this works, but I think a better solution is to add some kind of field/state
 * to the backend (i.e. `completed_at`) to be able to explicitly check if a search
 * is still running, vs there was a legit problem and we didn't get any data back.
 */
function isSkeletonResponse(v: any): boolean {
  if (isPlainObject(v)) {
    return isEmpty(v) ? true : Object.values(v).every(isSkeletonResponse);
  }

  if (isArray(v)) {
    return isEmpty(v) ? true : v.every(isSkeletonResponse);
  }

  return isNil(v) || v === false;
}

export default function LookupCompany() {
  const api = useTrueBizApi();
  const { searchId, domain } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [searchIncomplete, setSearchIncomplete] = useState(false);
  const [failed, setFailed] = useState<boolean>(false);

  const [companyLookup, setCompanyLookup] = useState<null | APICompanyLookup>(
    null
  );

  const [lastRequestStatus, setLastRequestStatus] = useState<null | number>(
    null
  );

  const onResetState = () => {
    setCompanyLookup(null);
    setLastRequestStatus(null);
    setSearchIncomplete(false);
  };

  useEffect(() => {
    if (!searchId) return;

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setSearchIncomplete(false);

    class SearchIncompleteError extends Error {}

    backOff(
      async () => {
        let companyLookup;

        try {
          companyLookup = await api.getCompanyLookup(searchId);
        } catch (e) {
          if (cancelled) return;

          if (api.isResponseError(e)) {
            setLastRequestStatus(e.status);
          }
          return;
        }

        if (cancelled) return;

        setLastRequestStatus(200);

        if (isSkeletonResponse(companyLookup.response_data)) {
          setSearchIncomplete(true);
          throw new SearchIncompleteError();
        }

        setSearchIncomplete(false);
        setCompanyLookup(companyLookup);
      },
      {
        maxDelay: 30_000,
        startingDelay: 1_000,
        delayFirstAttempt: false,
        retry: (e, attemptNumber) => {
          if (e instanceof SearchIncompleteError) {
            console.error(
              `[getCompanyLookup]: attempt ${attemptNumber} failed, search is incomplete, retrying.`
            );
            return !cancelled;
          }
          console.error(`[getCompanyLookup]: ${e}`);
          return false;
        },
      }
    )
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setSearchIncomplete(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, searchId]);

  const encodedDomain = encodeURIComponent(String(domain));

  const pathPrefix = generatePath("/search/:domain/:searchId", {
    searchId: encodeURIComponent(String(searchId)),
    domain: encodedDomain,
  });

  return (
    <div>
      <NetworkFailureToast open={failed} onClose={() => setFailed(false)} />

      <WebOnly>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          <NavLink to={`/searches?${searchParams.toString()}`}>
            <ArrowBackIcon />
          </NavLink>{" "}
          Previously Run Search
        </Typography>
      </WebOnly>

      <Box marginBottom={3} marginTop={1}>
        {loading || !companyLookup ? (
          searchIncomplete ? (
            <Alert severity="info">
              We&rsquo;re still analyzing all of the data for this search. This
              page will refresh when your search completes. Please check back in
              a few minutes.
            </Alert>
          ) : (
            <Skeleton variant="rectangular" width={650} height={24} />
          )
        ) : (
          <div>
            Showing previous search result from{" "}
            {humanizeDateString(companyLookup.created_at)}.{" "}
            <WebOnly>
              <NavLink
                to={`/domain/${encodedDomain}`}
                style={{ marginLeft: "0.25em" }}
              >
                See Most Recent Data
              </NavLink>
              <DownloadPdfButton
                requestId={companyLookup?.id}
                sx={{ float: "right" }}
              />
            </WebOnly>
          </div>
        )}
      </Box>

      <TextField
        fullWidth
        disabled={loading || searchIncomplete}
        label="Domain Name"
        type="text"
        value={domain}
        inputProps={{
          style: {
            cursor: loading ? "default" : "pointer",
          },
          onClick: () => {
            navigate(`/domain/${encodedDomain}`);
          },
        }}
        InputProps={{
          readOnly: true,
        }}
      />

      <UnsuccessfulErrorDisplay
        lastRequestStatus={lastRequestStatus}
        callToActionComponent={
          <NavLink to="json">View the raw JSON response</NavLink>
        }
      />
      <UnauthorizedErrorDisplay
        lastRequestStatus={lastRequestStatus}
        onResetState={onResetState}
      />

      {companyLookup && !searchIncomplete && (
        <Slide direction="up" in>
          <Box marginTop={3}>
            <ResultsTabs
              pathPrefix={pathPrefix}
              response={companyLookup.response_data}
            />

            <Outlet
              context={{ response: companyLookup.response_data, domain }}
            />
          </Box>
        </Slide>
      )}
    </div>
  );
}
